import { useState, useRef, useEffect, useCallback } from "react";
import {
  INTERSECTION_THRESHOLD,
  ROOT_MARGIN,
} from "../modules/GradesConstants";

/**
 * Tracks visibility of grade DOM elements using IntersectionObserver.
 * Maintains a set of currently visible grade IDs.
 *
 * @param {boolean} viewOnly - If true, disables the observer
 * @returns {{ visibleGradeIds: string[], registerGradeRef: Function }}
 */
const useVisibleGrades = viewOnly => {
  const [visibleGradeIds, setVisibleGradeIds] = useState([]);
  const gradesRef = useRef({});
  const observerRef = useRef(null);

  const observeElement = useCallback((element, observer) => {
    if (element && element.nodeType === Node.ELEMENT_NODE && observer) {
      observer.observe(element);
    }
  }, []);

  const unobserveElement = useCallback((element, observer) => {
    if (element && element.nodeType === Node.ELEMENT_NODE && observer) {
      observer.unobserve(element);
    }
  }, []);

  useEffect(() => {
    if (viewOnly) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        const visibleIds = [];
        const hiddenIds = [];

        entries.forEach(entry => {
          const elementId = entry.target.getAttribute("data-id");
          if (elementId) {
            if (entry.isIntersecting) {
              visibleIds.push(elementId);
            } else {
              hiddenIds.push(elementId);
            }
          }
        });

        if (visibleIds.length > 0 || hiddenIds.length > 0) {
          setVisibleGradeIds(prevIds => {
            const updatedIds = new Set(prevIds);
            visibleIds.forEach(id => updatedIds.add(id));
            hiddenIds.forEach(id => updatedIds.delete(id));
            return Array.from(updatedIds);
          });
        }
      },
      {
        threshold: INTERSECTION_THRESHOLD,
        rootMargin: ROOT_MARGIN,
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [viewOnly]);

  const registerGradeRef = useCallback(
    (id, element) => {
      if (element) {
        gradesRef.current[id] = element;
        if (observerRef.current) {
          observeElement(element, observerRef.current);
        }
      } else {
        const oldElement = gradesRef.current[id];
        if (oldElement && observerRef.current) {
          unobserveElement(oldElement, observerRef.current);
        }
        delete gradesRef.current[id];
      }
    },
    [observeElement, unobserveElement]
  );

  return {
    visibleGradeIds,
    registerGradeRef,
  };
};

export default useVisibleGrades;
